import { AddFairCandidatesResponseDto } from '@/api/generated';
import ButtonWithLoading from '@/components/ButtonWithLoading.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { TONE_STYLES } from '@/views/admin/candidates/candidateStatus.ts';
import { COLORS, formatNumber } from '@/views/admin/dashboard/dashboardTheme.ts';
import { MAX_EMAILS, parseEmails } from '@/views/admin/fairs/fairs.ts';
import { useAddFairCandidates } from '@/views/admin/fairs/useFairsApi.util.ts';
import React, { useId, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { IoAlertCircle, IoCheckmarkCircle, IoEllipse } from 'react-icons/io5';

const MAX_NOTE_LENGTH = 500;

interface Props {
  adminUserId: string;
  /** Fair the modal starts with, e.g. the one selected in the results */
  defaultFair?: string;
  fairNames: string[];
  language: string;
  onHide: () => void;
  show: boolean;
}

export const AddFairCandidatesModal: React.FC<Props> = ({
  adminUserId,
  defaultFair,
  fairNames,
  language,
  onHide,
  show,
}) => {
  const { t } = useTranslationWithPrefix('views.admin.fairs.bulk');
  const { addFairCandidates, isAdding, reset } = useAddFairCandidates(adminUserId);
  const fairListId = useId();
  const [text, setText] = useState('');
  const [fair, setFair] = useState(defaultFair ?? '');
  const [result, setResult] = useState<AddFairCandidatesResponseDto>();
  const [failed, setFailed] = useState(false);

  const emails = parseEmails(text);
  const tooMany = emails.length > MAX_EMAILS;

  const handleEnter = () => {
    setText('');
    setFair(defaultFair ?? '');
    setResult(undefined);
    setFailed(false);
    reset();
  };

  const addMore = () => {
    setText('');
    setResult(undefined);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFailed(false);
    try {
      setResult(await addFairCandidates({ emails, note: fair.trim() || undefined }));
    } catch {
      setFailed(true);
    }
  };

  return (
    <Modal onEnter={handleEnter} onHide={onHide} show={show} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{result ? t('result.title') : t('title')}</Modal.Title>
      </Modal.Header>
      {result ? (
        <>
          <Modal.Body>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              <ResultRow
                emails={[]}
                icon={<IoCheckmarkCircle aria-hidden />}
                label={t('result.added', { count: formatNumber(result.added.length, language) })}
                tone="good"
              />
              {result.alreadyAdded.length > 0 && (
                <ResultRow
                  emails={result.alreadyAdded}
                  icon={<IoEllipse aria-hidden size={8} />}
                  label={t('result.alreadyAdded', {
                    count: formatNumber(result.alreadyAdded.length, language),
                  })}
                  tone="neutral"
                />
              )}
              {result.invalid.length > 0 && (
                <ResultRow
                  emails={result.invalid}
                  icon={<IoAlertCircle aria-hidden />}
                  label={t('result.invalid', {
                    count: formatNumber(result.invalid.length, language),
                  })}
                  tone="critical"
                />
              )}
            </ul>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-outline-secondary" onClick={addMore} type="button">
              {t('addMore')}
            </button>
            <button className="btn btn-primary" onClick={onHide} type="button">
              {t('close')}
            </button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={submit}>
          <Modal.Body>
            {failed && <Alert variant="danger">{t('error')}</Alert>}
            <p className="mb-3" style={{ color: COLORS.inkSecondary }}>
              {t('description')}
            </p>
            <Form.Group className="mb-1" controlId="fair-candidates-emails">
              <Form.Label>{t('emailsLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                autoFocus
                disabled={isAdding}
                onChange={event => setText(event.target.value)}
                placeholder={t('emailsPlaceholder')}
                rows={8}
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  fontSize: 14,
                  textAlign: 'left',
                }}
                value={text}
              />
            </Form.Group>
            <div
              aria-live="polite"
              className="mb-4 text-sm font-bold"
              style={{ color: tooMany ? TONE_STYLES.critical.color : COLORS.inkSecondary }}
            >
              {t('found', { count: formatNumber(emails.length, language) })}
              {tooMany && ` · ${t('tooMany', { max: formatNumber(MAX_EMAILS, language) })}`}
            </div>
            <Form.Group controlId="fair-candidates-fair">
              <Form.Label>{t('fairLabel')}</Form.Label>
              <Form.Control
                disabled={isAdding}
                list={fairListId}
                maxLength={MAX_NOTE_LENGTH}
                onChange={event => setFair(event.target.value)}
                placeholder={t('fairPlaceholder')}
                value={fair}
              />
              <datalist id={fairListId}>
                {fairNames.map(name => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <Form.Text style={{ color: COLORS.inkMuted }}>{t('fairHint')}</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-secondary"
              disabled={isAdding}
              onClick={onHide}
              type="button"
            >
              {t('cancel')}
            </button>
            <ButtonWithLoading
              className="btn btn-primary"
              disabled={emails.length === 0 || tooMany}
              isLoading={isAdding}
              type="submit"
            >
              {t('submit')}
            </ButtonWithLoading>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  );
};

interface ResultRowProps {
  emails: string[];
  icon: React.ReactNode;
  label: string;
  tone: keyof typeof TONE_STYLES;
}

const ResultRow: React.FC<ResultRowProps> = ({ emails, icon, label, tone }) => (
  <li>
    <span
      className="inline-flex items-center gap-2 px-3 py-2 font-bold"
      style={{ ...TONE_STYLES[tone], borderRadius: 4 }}
    >
      {icon}
      {label}
    </span>
    {emails.length > 0 && (
      <div
        className="mt-2 max-h-[120px] overflow-y-auto text-sm break-all"
        style={{ color: COLORS.inkSecondary }}
      >
        {emails.join(', ')}
      </div>
    )}
  </li>
);
